import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Compass, Send, Loader2, Plus, Trash2, Home, ChevronLeft, ChevronRight, MessageSquare, ArrowLeft, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AuthContext, API } from "@/App";
import { toast } from "sonner";

const ChatPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const messagesEndRef = useRef(null);

  const tier = user?.subscription_tier || "free";
  const hasAccess = user?.is_admin || user?.is_vip || ["pro", "premium", "team"].includes(tier);

  useEffect(() => { fetchSessions(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const fetchSessions = async () => {
    try {
      const res = await fetch(`${API}/chat/sessions`, { credentials: "include" });
      if (res.ok) setSessions(await res.json());
    } catch (e) {} finally { setLoadingSessions(false); }
  };

  const loadSession = async (sid) => {
    setSessionId(sid);
    try {
      const res = await fetch(`${API}/chat/${sid}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (e) { toast.error("Failed to load chat"); }
  };

  const startNewChat = () => {
    setSessionId(null);
    setMessages([]);
  };

  const deleteSession = async (sid) => {
    try {
      await fetch(`${API}/chat/${sid}`, { method: "DELETE", credentials: "include" });
      setSessions(sessions.filter(s => s.session_id !== sid));
      if (sessionId === sid) startNewChat();
      toast.success("Chat deleted");
    } catch (e) { toast.error("Failed to delete"); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || loading) return;

    const userMsg = message.trim();
    setMessage("");
    setMessages(prev => [...prev, { role: "user", content: userMsg, timestamp: new Date().toISOString() }]);
    setLoading(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: userMsg, session_id: sessionId })
      });
      if (res.ok) {
        const data = await res.json();
        setSessionId(data.session_id);
        setMessages(prev => [...prev, { role: "assistant", content: data.response, timestamp: new Date().toISOString() }]);
        fetchSessions();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Chat failed");
        setMessages(prev => prev.slice(0, -1));
      }
    } catch (e) {
      toast.error("Something went wrong");
      setMessages(prev => prev.slice(0, -1));
    } finally { setLoading(false); }
  };

  const renderMarkdown = (text) => {
    const parts = text.split(/(```[\s\S]*?```)/g);
    return parts.map((part, i) => {
      if (part.startsWith("```")) {
        const code = part.replace(/```\w*\n?/, "").replace(/```$/, "");
        return <pre key={i} className="bg-dark rounded-lg p-3 my-2 overflow-x-auto font-mono text-xs text-white/80 border border-white/5">{code}</pre>;
      }
      return part.split("\n").map((line, j) => {
        if (line.startsWith("### ")) return <h3 key={`${i}-${j}`} className="font-semibold text-sm mt-3 mb-1">{line.slice(4)}</h3>;
        if (line.startsWith("## ")) return <h2 key={`${i}-${j}`} className="font-semibold text-base mt-3 mb-1">{line.slice(3)}</h2>;
        if (line.startsWith("# ")) return <h1 key={`${i}-${j}`} className="font-bold text-lg mt-3 mb-1">{line.slice(2)}</h1>;
        if (line.startsWith("- ") || line.startsWith("* ")) return <li key={`${i}-${j}`} className="ml-4 text-sm text-white/70">{renderInline(line.slice(2))}</li>;
        if (line.match(/^\d+\.\s/)) return <li key={`${i}-${j}`} className="ml-4 text-sm text-white/70 list-decimal">{renderInline(line.replace(/^\d+\.\s/, ""))}</li>;
        if (line.trim() === "") return <div key={`${i}-${j}`} className="h-1.5" />;
        return <p key={`${i}-${j}`} className="text-sm text-white/70 leading-relaxed">{renderInline(line)}</p>;
      });
    });
  };

  const renderInline = (text) => {
    return text.split(/(`[^`]+`)/).map((part, i) => {
      if (part.startsWith("`") && part.endsWith("`")) return <code key={i} className="bg-purple-900/30 text-purple-300 px-1 py-0.5 rounded text-xs font-mono">{part.slice(1, -1)}</code>;
      return part.split(/(\*\*[^*]+\*\*)/).map((p2, j) => {
        if (p2.startsWith("**") && p2.endsWith("**")) return <strong key={`${i}-${j}`} className="font-semibold text-white/90">{p2.slice(2, -2)}</strong>;
        return p2;
      });
    });
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <Lock className="w-12 h-12 text-purple-400 mx-auto mb-4" />
          <h2 className="font-inter font-bold text-xl mb-2">Chat with Promptviser</h2>
          <p className="text-white/50 text-sm mb-6">This feature is available for Pro subscribers and above.</p>
          <div className="flex gap-3 justify-center">
            <Link to="/pricing"><Button className="bg-purple-700 text-white hover:bg-purple-600">Upgrade to Pro - $12/mo</Button></Link>
            <Link to="/"><Button variant="outline" className="border-white/20">Go Home</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-dark flex flex-col">
      {/* Header */}
      <header className="border-b border-white/10 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white/60 hover:text-white"><ChevronLeft className="w-5 h-5" /></Button>
              <Link to="/" className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-purple-700 flex items-center justify-center"><Compass className="w-3.5 h-3.5 text-white" /></div>
                <span className="font-inter font-bold text-sm hidden sm:inline">promptviser</span>
              </Link>
              <Badge className="bg-purple-500/15 text-purple-300 border-purple-500/30 text-[10px]">Chat</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={startNewChat} className="border-white/20 text-xs" data-testid="new-chat-button"><Plus className="w-3.5 h-3.5 mr-1" />New Chat</Button>
              <Link to="/"><Button variant="ghost" size="icon" className="text-white/60 hover:text-white"><Home className="w-4 h-4" /></Button></Link>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-64 border-r border-white/10 flex-shrink-0 overflow-y-auto hidden md:block">
          <div className="p-3">
            <Button variant="outline" onClick={startNewChat} className="w-full border-white/10 text-xs mb-3"><Plus className="w-3.5 h-3.5 mr-1" />New Chat</Button>
            {loadingSessions ? (
              <div className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin text-white/30 mx-auto" /></div>
            ) : sessions.length === 0 ? (
              <p className="text-white/30 text-xs text-center py-4">No chats yet</p>
            ) : (
              <div className="space-y-1">
                {sessions.map(s => (
                  <div key={s.session_id} className={`group flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${sessionId === s.session_id ? "bg-purple-900/30 border border-purple-500/20" : "hover:bg-white/5"}`} onClick={() => loadSession(s.session_id)}>
                    <MessageSquare className="w-3.5 h-3.5 text-white/30 flex-shrink-0" />
                    <span className="text-xs text-white/60 truncate flex-1">{s.messages?.[0]?.content?.slice(0, 40) || "Chat"}</span>
                    <button onClick={(e) => { e.stopPropagation(); deleteSession(s.session_id); }} className="opacity-0 group-hover:opacity-100"><Trash2 className="w-3 h-3 text-white/30 hover:text-red-400" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6">
            {messages.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center max-w-lg">
                  <div className="w-14 h-14 rounded-2xl bg-purple-900/30 border border-purple-500/20 flex items-center justify-center mx-auto mb-4">
                    <Compass className="w-7 h-7 text-purple-400" />
                  </div>
                  <h2 className="font-inter font-semibold text-xl mb-2">Chat with Promptviser</h2>
                  <p className="text-white/40 text-sm mb-6">Ask me to refine your prompts, find the right AI tool, or explain prompt engineering techniques.</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {["Optimize my prompt for SEO writing", "Which AI is best for Swift coding?", "Create a Chain-of-Thought prompt", "Compare Claude vs GPT for my task"].map((q, i) => (
                      <button key={i} onClick={() => { setMessage(q); }} className="text-xs text-white/40 border border-white/10 px-3 py-1.5 rounded-full hover:border-purple-500/30 hover:text-purple-300 transition-colors" data-testid={`suggestion-${i}`}>{q}</button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="max-w-3xl mx-auto space-y-4">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-xl px-4 py-3 ${msg.role === "user" ? "bg-purple-700 text-white" : "bg-dark-paper border border-white/10"}`} data-testid={`chat-message-${i}`}>
                      {msg.role === "user" ? (
                        <p className="text-sm">{msg.content}</p>
                      ) : (
                        <div>{renderMarkdown(msg.content)}</div>
                      )}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-dark-paper border border-white/10 rounded-xl px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-neon rounded-full animate-neon-pulse" />
                        <span className="text-white/40 text-xs">Thinking...</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input */}
          <div className="flex-shrink-0 border-t border-white/10 px-4 md:px-8 py-4">
            <form onSubmit={sendMessage} className="max-w-3xl mx-auto">
              <div className="flex items-center gap-3 bg-dark-paper border border-white/10 rounded-xl px-4 py-2">
                <Input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Ask Promptviser anything..."
                  className="flex-1 bg-transparent border-0 focus-visible:ring-0 text-sm placeholder:text-white/25"
                  disabled={loading}
                  data-testid="chat-input"
                />
                <Button type="submit" size="icon" disabled={loading || !message.trim()} className="bg-purple-700 text-white hover:bg-purple-600 rounded-lg h-8 w-8" data-testid="chat-send-button">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-center text-[10px] text-white/20 mt-2">Promptviser may make mistakes. Verify important information.</p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
